import { useCallback, useEffect, useRef, useState } from "react"

type Props = {
  conversationId?: string | null
  lastConversationId?: string
  onConversationSelect: (conversationId?: string) => void
}

export function useExpandedConversationId({
  conversationId,
  lastConversationId,
  onConversationSelect,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | undefined>(() => {
    return conversationId ?? lastConversationId
  })
  const [pendingExpandedId, setPendingExpandedId] = useState<string | undefined>(undefined)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const prevConversationIdRef = useRef(conversationId)

  useEffect(() => {
    if (expandedId || conversationId || isCollapsed || !lastConversationId) return
    setExpandedId(lastConversationId)
  }, [conversationId, expandedId, isCollapsed, lastConversationId])

  useEffect(() => {
    if (conversationId === prevConversationIdRef.current) return
    prevConversationIdRef.current = conversationId

    if (!conversationId) return

    setIsCollapsed(false)

    if (pendingExpandedId) {
      if (conversationId === pendingExpandedId) {
        setPendingExpandedId(undefined)
        setExpandedId(conversationId)
      }
      return
    }

    setExpandedId(conversationId)
  }, [conversationId, pendingExpandedId])

  const expandConversation = useCallback(
    (nextConversationId: string) => {
      setIsCollapsed(false)
      setExpandedId(nextConversationId)
      setPendingExpandedId(conversationId === nextConversationId ? undefined : nextConversationId)
      onConversationSelect(nextConversationId)
    },
    [conversationId, onConversationSelect],
  )

  const collapseConversation = useCallback(() => {
    setIsCollapsed(true)
    setPendingExpandedId(undefined)
    setExpandedId(undefined)
    onConversationSelect(undefined)
  }, [onConversationSelect])

  return {
    expandedId,
    expandConversation,
    collapseConversation,
  }
}
